import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { MessagePrev } from "@/types";
import {
  getButtonStyles,
  selectCompetenceStyles,
  selectStyles,
  emptyStateContainerStyles,
} from "./styles";
import { ContentCard } from "../content-card/content-card";
import { UnreadLessonsPanel } from "../unread-lessons-panel/unread-lessons-panel";
import { useStatisticsStore } from "../../../stores/statisticsStore";
import { useChatStore } from "../../../stores/chatStore";

export type LessonsViewMode = "unread" | "score" | "competence" | "all";

interface LessonsPanelProps {
  lessons: MessagePrev[];
  isLoading: boolean;
  error: string | null;
  lessonsByScore: MessagePrev[];
  isLoadingLessonsByScore: boolean;
  errorLessonsByScore: string | null;
  fetchLessonsByScore: (score: string) => Promise<void>;
  onLessonClick: (lessonId: string, domain: string, focus: string) => void;
  initialViewMode?: LessonsViewMode;
}

export const LessonsPanel = ({
  lessons,
  isLoading,
  error,
  lessonsByScore,
  isLoadingLessonsByScore,
  errorLessonsByScore,
  fetchLessonsByScore,
  onLessonClick,
  initialViewMode,
}: LessonsPanelProps) => {
  const [viewMode, setViewMode] = useState<LessonsViewMode>(
    initialViewMode || "unread",
  );
  const [selectedScore, setSelectedScore] = useState<string>("0");
  const [selectedCompetence, setSelectedCompetence] = useState<string>("");
  const [competenceMenuWidth, setCompetenceMenuWidth] = useState<
    number | undefined
  >(undefined);

  const {
    lessonsByCompetence,
    isLoadingLessonsByCompetence,
    errorLessonsByCompetence,
    fetchLessonsByCompetence,
    fetchLessonsToDo,
  } = useChatStore();

  const { competencies, fetchCompetencies } = useStatisticsStore();

  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const el = buttonContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  useEffect(() => {
    const el = buttonContainerRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [viewMode, lessons, lessonsByScore, competencies]);

  const scrollButtons = (direction: "left" | "right") => {
    const el = buttonContainerRef.current;
    if (!el) return;
    const scrollAmount = 200;
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (viewMode === "competence" && competencies.length === 0) {
      fetchCompetencies();
    }
  }, [viewMode, competencies, fetchCompetencies]);

  useEffect(() => {
    if (viewMode === "unread") {
      fetchLessonsToDo();
    }
  }, [viewMode, fetchLessonsToDo]);

  const handleScoreChange = async (e: SelectChangeEvent<string>) => {
    const newScore = e.target.value;
    setSelectedScore(newScore);
    await fetchLessonsByScore(newScore);
  };

  const handleCompetenceChange = async (e: SelectChangeEvent<string>) => {
    const newCompetence = e.target.value;
    setSelectedCompetence(newCompetence);
    await fetchLessonsByCompetence(newCompetence);
  };

  const handleCompetenceOpen = (e: React.SyntheticEvent) => {
    const el = e.currentTarget as HTMLElement | null;
    if (!el) return;
    setCompetenceMenuWidth(el.getBoundingClientRect().width);
  };

  const renderEmptyState = () => (
    <Box sx={emptyStateContainerStyles}>
      <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
        No lessons available
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: "300px" }}
      >
        Select the option "Retrieve a lesson" in the bottom left menu to
        generate the New Lesson
      </Typography>
    </Box>
  );

  const renderLessonsList = (items: MessagePrev[]) => {
    if (!items || items.length === 0) {
      return renderEmptyState();
    }
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {items.map((item) => (
          <ContentCard
            key={item.message_id}
            item={item}
            onClick={onLessonClick}
            badgeLabel="Lesson"
          />
        ))}
      </Box>
    );
  };

  const renderAllLessons = () => {
    if (isLoading) {
      return <Typography>Loading...</Typography>;
    }
    if (error) {
      return <Typography color="error">{error}</Typography>;
    }
    return renderLessonsList(lessons);
  };

  const renderLessonsByScore = () => {
    if (isLoadingLessonsByScore)
      return <Typography>Loading (by score)...</Typography>;
    if (errorLessonsByScore)
      return <Typography color="error">{errorLessonsByScore}</Typography>;
    return renderLessonsList(lessonsByScore);
  };

  const renderLessonsByCompetence = () => {
    if (isLoadingLessonsByCompetence)
      return <Typography>Loading (by competence)...</Typography>;
    if (errorLessonsByCompetence)
      return <Typography color="error">{errorLessonsByCompetence}</Typography>;
    return renderLessonsList(lessonsByCompetence);
  };

  const renderUnreadLessons = () => (
    <UnreadLessonsPanel onMessageClick={onLessonClick} />
  );

  return (
    <Box sx={{ p: "0 10px" }}>
      <Box sx={{ position: "relative", mb: 2, px: "20px" }}>
        {canScrollLeft && (
          <IconButton
            onClick={() => scrollButtons("left")}
            sx={{
              position: "absolute",
              left: -25,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        <Box
          ref={buttonContainerRef}
          sx={{
            display: "flex",
            gap: 3,
            overflowX: "auto",
            whiteSpace: "nowrap",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Button
            onClick={() => setViewMode("unread")}
            sx={getButtonStyles(viewMode === "unread")}
          >
            Unread Lessons
          </Button>
          <Button
            onClick={() => setViewMode("all")}
            sx={getButtonStyles(viewMode === "all")}
          >
            All Lessons
          </Button>
          <Button
            onClick={() => setViewMode("score")}
            sx={getButtonStyles(viewMode === "score")}
          >
            Lessons by Score
          </Button>
          <Button
            onClick={() => setViewMode("competence")}
            sx={getButtonStyles(viewMode === "competence")}
          >
            Lessons by Competence
          </Button>
        </Box>

        {canScrollRight && (
          <IconButton
            onClick={() => scrollButtons("right")}
            sx={{
              position: "absolute",
              right: -25,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      {viewMode === "score" && (
        <Box mb={2}>
          <FormControl size="small">
            <InputLabel
              sx={{ "&.Mui-focused": { color: "#06B6D4" } }}
              id="score-select-label"
            >
              Select score
            </InputLabel>
            <Select
              labelId="score-select-label"
              label="Select score"
              value={selectedScore}
              onChange={handleScoreChange}
              sx={selectStyles}
            >
              {["0", "1", "2", "3", "4", "5"].map((val) => (
                <MenuItem key={val} value={val}>
                  {val}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {viewMode === "competence" && (
        <Box sx={{ width: "100%" }} mb={2}>
          <FormControl sx={{ flex: 1, display: "flex" }} size="small">
            <InputLabel
              sx={{ "&.Mui-focused": { color: "#06B6D4" }, display: "flex" }}
              id="competence-select-label"
            >
              Select competence
            </InputLabel>
            <Select
              variant="outlined"
              labelId="competence-select-label"
              label="Select competence"
              value={selectedCompetence}
              onChange={handleCompetenceChange}
              onOpen={handleCompetenceOpen}
              sx={selectCompetenceStyles}
              MenuProps={{
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                PaperProps: {
                  sx: {
                    width: competenceMenuWidth,
                    maxWidth: competenceMenuWidth,
                    maxHeight: 320,
                    overflowX: "auto",
                    overflowY: "auto",
                  },
                },
                MenuListProps: {
                  sx: {
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                  },
                },
              }}
            >
              {competencies.map((comp) => (
                <MenuItem
                  key={comp.competency}
                  value={comp.competency}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {comp.competency}
                  {comp?.score}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {viewMode === "unread"
        ? renderUnreadLessons()
        : viewMode === "all"
          ? renderAllLessons()
          : viewMode === "score"
            ? renderLessonsByScore()
            : renderLessonsByCompetence()}
    </Box>
  );
};
